import {
	type ContractSuretyAccountUnderwritingModelSize,
	Dtos,
	type SuretyAccountId,
} from "@inrev/common";
import { useAuthenticatedUser } from "@portal/providers/AuthenticatedUserProvider";
import { type FC, useMemo, useState } from "react";
import { WorkspaceWrapper, type WorkspaceWrapperTab } from "../patterns/workspace-wrapper";
import { AnalysisGrid } from "./AnalysisGrid";
import { BalanceSheetGrid } from "./BalanceSheetGrid";
import {
	type ActiveStatementType,
	FinancialsPeriodSelectorProvider,
} from "./FinancialsPeriodSelectorProvider";
import { IncomeStatementGrid } from "./IncomeStatementGrid";
import { ActiveStatementTypeEnum } from "./types";
import { type AnalysisSourceLookup, statementSource } from "./utils/analysis/sources";
import { type WipStatementForAnalysis, wipSource } from "./utils/analysis/wipSource";
import { buildBalanceSheetViewModel } from "./utils/buildBalanceSheetViewModel";
import { buildIncomeStatementViewModel } from "./utils/buildIncomeStatementViewModel";
import {
	buildAnalysisPeriodSelectorItems,
	buildBalanceSheetPeriodSelectorItems,
	buildIncomeStatementPeriodSelectorItems,
} from "./utils/buildPeriodSelectorItems";
import { mergeBalanceSheetWithEdits } from "./utils/mergeBalanceSheetWithEdits";
import { mergeIncomeStatementWithEdits } from "./utils/mergeIncomeStatementWithEdits";
import {
	sortedUsableBalanceSheets,
	sortedUsableIncomeStatements,
} from "./utils/sortFinancialStatements";

type StatementMatrixGridWithAdjustmentsProps = {
	accountId: SuretyAccountId;
	financialPeriods: Dtos.Admin.FinancialPeriod.Get.Response[];
	balanceSheets: Dtos.Admin.BalanceSheet.Get.Response[];
	incomeStatements: Dtos.Admin.IncomeStatement.Get.Response[];
	wipStatements?: WipStatementForAnalysis[];
	selectionResetKey?: string;
	modelSize?: ContractSuretyAccountUnderwritingModelSize | null;
};

const EMPTY_WIP_STATEMENTS: WipStatementForAnalysis[] = [];

const BASE_TABS: WorkspaceWrapperTab<ActiveStatementType>[] = [
	{ id: ActiveStatementTypeEnum.BalanceSheet, label: "Balance Sheet" },
	{ id: ActiveStatementTypeEnum.IncomeStatement, label: "Income Statement" },
];

const ANALYSIS_TAB: WorkspaceWrapperTab<ActiveStatementType> = {
	id: ActiveStatementTypeEnum.Analysis,
	label: "Analysis",
};

export const StatementMatrixGridWithAdjustments: FC<StatementMatrixGridWithAdjustmentsProps> = ({
	accountId,
	financialPeriods,
	balanceSheets,
	incomeStatements,
	wipStatements = EMPTY_WIP_STATEMENTS,
	selectionResetKey,
	modelSize,
}) => {
	const { isAdmin } = useAuthenticatedUser();
	const [active, setActive] = useState<ActiveStatementType>(ActiveStatementTypeEnum.BalanceSheet);

	const showAnalysisTab = isAdmin;
	const tabs = useMemo(
		() => (showAnalysisTab ? [...BASE_TABS, ANALYSIS_TAB] : BASE_TABS),
		[showAnalysisTab],
	);

	const sortedBalanceSheets = useMemo(
		() => sortedUsableBalanceSheets(balanceSheets),
		[balanceSheets],
	);
	const sortedIncomeStatements = useMemo(
		() => sortedUsableIncomeStatements(incomeStatements),
		[incomeStatements],
	);
	const balanceSheetBaseViewModel = useMemo(
		() => buildBalanceSheetViewModel(sortedBalanceSheets, financialPeriods),
		[sortedBalanceSheets, financialPeriods],
	);
	const incomeStatementBaseViewModel = useMemo(
		() => buildIncomeStatementViewModel(sortedIncomeStatements, financialPeriods, { isAdmin }),
		[sortedIncomeStatements, financialPeriods, isAdmin],
	);
	const balanceSheetPeriodSelectorItems = useMemo(
		() => buildBalanceSheetPeriodSelectorItems(sortedBalanceSheets, financialPeriods),
		[sortedBalanceSheets, financialPeriods],
	);
	const incomeStatementPeriodSelectorItems = useMemo(
		() => buildIncomeStatementPeriodSelectorItems(sortedIncomeStatements, financialPeriods),
		[sortedIncomeStatements, financialPeriods],
	);
	const analysisPeriodSelectorItems = useMemo(
		() => buildAnalysisPeriodSelectorItems(financialPeriods),
		[financialPeriods],
	);

	// Pluggable data sources for the analysis grid. Adding a source (WIP, Debt, …) is a single entry
	// here: build its view model and bind its active-statement-id accessor. A null view model keeps the
	// source registered but empty, so metrics requiring it resolve to "--" until the data is wired.
	const analysisSources = useMemo<AnalysisSourceLookup[]>(
		() => [
			statementSource(
				"balanceSheet",
				mergeBalanceSheetWithEdits({ viewModel: balanceSheetBaseViewModel }),
				(p) => p.activeBalanceSheetId ?? null,
			),
			statementSource(
				"incomeStatement",
				mergeIncomeStatementWithEdits({ viewModel: incomeStatementBaseViewModel }),
				(p) => p.activeIncomeStatementId ?? null,
			),
			wipSource(wipStatements),
			// TODO(Debt): decide BS-derived vs. its own schedule, then bind here.
			statementSource("debt", null, () => null),
		],
		[balanceSheetBaseViewModel, incomeStatementBaseViewModel, wipStatements],
	);

	return (
		<FinancialsPeriodSelectorProvider
			key={selectionResetKey}
			balanceSheetPeriodSelectorItems={balanceSheetPeriodSelectorItems}
			incomeStatementPeriodSelectorItems={incomeStatementPeriodSelectorItems}
			analysisPeriodSelectorItems={analysisPeriodSelectorItems}
			activeStatementType={active}
		>
			<WorkspaceWrapper
				tabs={tabs}
				activeTabId={active}
				onTabChange={setActive}
				tabListLabel="Statement type"
				testId="financials-workspace"
				trackingId="account-financials-workspace"
			>
				{active === ActiveStatementTypeEnum.BalanceSheet && (
					<BalanceSheetGrid accountId={accountId} baseViewModel={balanceSheetBaseViewModel} />
				)}
				{active === ActiveStatementTypeEnum.IncomeStatement && (
					<IncomeStatementGrid accountId={accountId} baseViewModel={incomeStatementBaseViewModel} />
				)}
				{active === ActiveStatementTypeEnum.Analysis && (
					<AnalysisGrid
						accountId={accountId}
						financialPeriods={financialPeriods}
						sources={analysisSources}
						modelSize={modelSize}
					/>
				)}
			</WorkspaceWrapper>
		</FinancialsPeriodSelectorProvider>
	);
};
